// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * "Del op" — the evidence tile's SAM3 tools — as pure data: the run
 * button's copy, the text prompts and label-library chips, the confidence
 * readout, the neighbour queue's targets, and the 360° flavour (text-only
 * prompts over equator tiles, `POST /panoramas/{id}/segment`), including the
 * "alle 360°" batch that stands in for `rux create annotate-360`.
 *
 * Pure: no React, no DOM.
 */

import type { PanoramaSegmentRequest, PanoramaSegmentResult } from '../api/types';
import type { Sam3Phase } from '../data/sam3Provisioning';
import { neighborFrameIds } from '../data/labelQueue';
import type { ResultClass, SegPrompt } from '../data/segmentView';

// ------------------------------------------------------------ the frame --

/**
 * The run button's label: what a click on it will do now. While a run is in
 * flight for another frame than the one shown (`runningFrame` ≠ `frameId`),
 * it names that frame, as the Segmentering page does (28f5006).
 */
export function runLabel(opts: {
  running: boolean;
  phase: Sam3Phase;
  promptCount: number;
  runningFrame?: number | null;
  frameId?: number;
}): string {
  if (opts.running) {
    if (opts.runningFrame != null && opts.frameId !== undefined && opts.runningFrame !== opts.frameId) {
      return `Segmenterer billede ${opts.runningFrame}…`;
    }
    return opts.phase === 'preparing' || opts.phase === 'first-run' ? 'Klargør model…' : 'Segmenterer…';
  }
  if (opts.promptCount === 0) return 'Kør med standardklasser';
  return opts.promptCount === 1 ? 'Kør segmentering' : `Kør segmentering (${opts.promptCount})`;
}

/** A confidence threshold as the slider shows it, Danish decimals ("0,50"). */
export function confidenceText(confidence: number): string {
  return confidence.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A text-only prompt appended (blank text allowed: the user types it next). */
export function withTextPrompt(prompts: readonly SegPrompt[], id: string, text = ''): SegPrompt[] {
  return [...prompts, { id, text, box: null, point: false }];
}

/** Label-library texts not already used as a text-only prompt (folded, trimmed). */
export function unusedLibraryTexts(library: readonly { text: string }[], prompts: readonly SegPrompt[]): string[] {
  const used = new Set(prompts.filter((p) => !p.box).map((p) => p.text.trim().toLowerCase()));
  const out: string[] = [];
  const seen = new Set<string>();
  for (const { text } of library) {
    const key = text.trim().toLowerCase();
    if (!key || used.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(text.trim());
  }
  return out;
}

/**
 * The disclosure's one-line summary, so a closed "Flere værktøjer" still
 * says what a run will send: "2 tekstprompts · konfidens 0,50".
 */
export function optionsSummary(prompts: readonly SegPrompt[], confidence: number, queued: number): string {
  const texts = prompts.filter((p) => !p.box && p.text.trim()).length;
  const named = prompts.filter((p) => p.box && p.text.trim()).length;
  const parts: string[] = [];
  if (texts > 0) parts.push(texts === 1 ? '1 tekstprompt' : `${texts} tekstprompts`);
  if (named > 0) parts.push(named === 1 ? '1 navngiven markering' : `${named} navngivne markeringer`);
  parts.push(`konfidens ${confidenceText(confidence)}`);
  if (queued > 0) parts.push(`${queued} i kø`);
  return parts.join(' · ');
}

/** The frames "Tilføj til kø" stages: `before`/`after` neighbours in scan order, this frame included. */
export function queueTargets(ids: readonly number[] | null, frameId: number, before: number, after: number): number[] {
  if (!ids || ids.length === 0) return [frameId];
  return neighborFrameIds([...ids], frameId, before, after);
}

/** "Tilføj 5 billeder til kø" — the button names the count it will add. */
export function queueButtonLabel(count: number): string {
  return `Tilføj ${count} ${count === 1 ? 'billede' : 'billeder'} til kø`;
}

// -------------------------------------------------------------- the 360° --

/** Equator tiles around the sphere: ruxd's default and the range offered. */
export const N_YAW_DEFAULT = 8;
export const N_YAW_MIN = 4;
export const N_YAW_MAX = 24;
/** Per-tile horizontal field of view (degrees). */
export const FOV_DEFAULT = 90;
export const FOV_MIN = 45;
export const FOV_MAX = 150;

function clampInt(raw: string | number, lo: number, hi: number, fallback: number): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
}

export const clampNYaw = (raw: string | number) => clampInt(raw, N_YAW_MIN, N_YAW_MAX, N_YAW_DEFAULT);
export const clampFov = (raw: string | number) => clampInt(raw, FOV_MIN, FOV_MAX, FOV_DEFAULT);

export interface PanoPrompt {
  id: string;
  text: string;
}

export interface PanoRequestOptions {
  confidence: number;
  nYaw: number;
  fovDeg: number;
}

/**
 * The panorama request: text prompts only (ruxd v1 takes no geometry in
 * equirect space); none left after trimming ⟹ the model's default classes.
 * Returns the texts sent too — their order is the label value in the saved
 * equirect mask, exactly as for a frame.
 */
export function panoRequest(
  prompts: readonly PanoPrompt[],
  opts: PanoRequestOptions,
): { request: PanoramaSegmentRequest; sent: string[] } {
  const sent = prompts.map((p) => p.text.trim()).filter(Boolean);
  const request: PanoramaSegmentRequest = {
    confidence: opts.confidence,
    n_yaw: clampNYaw(opts.nYaw),
    fov_deg: clampFov(opts.fovDeg),
    save: true,
  };
  if (sent.length > 0) request.prompts = sent.map((text) => ({ text }));
  return { request, sent };
}

/**
 * The panorama run's classes, as `ResultClass`es so the frame's resource
 * dialog takes them unchanged. Pixel counts are unknown (ruxd serves no
 * panorama mask image — CONTRACT-GAPS 15), so `pixels` stays null.
 */
export function panoResultClasses(result: PanoramaSegmentResult, sent: readonly string[]): ResultClass[] {
  const indices = new Set<number>(sent.map((_, i) => i));
  for (const key of Object.keys(result.labels)) {
    const n = Number(key);
    if (Number.isInteger(n) && n >= 0) indices.add(n);
  }
  return [...indices]
    .sort((a, b) => a - b)
    .map((index) => {
      const text = sent[index] ?? result.labels[String(index)] ?? '';
      return { index, promptId: null, name: text || `Klasse ${index + 1}`, className: text, pixels: null };
    });
}

// ---------------------------------------------- "alle 360°" (annotate-360) --

export type BatchItemStatus = 'pending' | 'running' | 'done' | 'failed';

export interface BatchItem {
  panoramaId: number;
  status: BatchItemStatus;
  labeledPixels?: number;
  error?: string;
}

/** One pending item per panorama, in the given order (duplicates dropped). */
export function batchItems(panoramaIds: readonly number[]): BatchItem[] {
  return [...new Set(panoramaIds)].map((panoramaId) => ({ panoramaId, status: 'pending' }));
}

/** The next item to run, or null when none is pending. */
export function nextPending(items: readonly BatchItem[]): BatchItem | null {
  return items.find((i) => i.status === 'pending') ?? null;
}

export function setBatchStatus(
  items: readonly BatchItem[],
  panoramaId: number,
  status: BatchItemStatus,
  extra: { labeledPixels?: number; error?: string } = {},
): BatchItem[] {
  return items.map((i) => (i.panoramaId === panoramaId ? { ...i, status, ...extra } : i));
}

/** "3 af 6 · 1 fejlet" — the batch's progress line. */
export function batchProgress(items: readonly BatchItem[]): string {
  const finished = items.filter((i) => i.status === 'done' || i.status === 'failed').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  return `${finished} af ${items.length}${failed > 0 ? ` · ${failed} fejlet` : ''}`;
}
